"use client";
import React, { useEffect, useRef, useState } from "react";
import Lenis from "lenis";

type ScrollableContentProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  height?: number | string;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  indicatorVariant?: "default" | "contrast";
  useLenis?: boolean;
  lenisDurationMs?: number;
  lenisLerp?: number;
  "data-overlay"?: string | boolean;
  disableBelow?: number;
};

const ScrollableContent: React.FC<ScrollableContentProps> = (props) => {
  const {
    children,
    height = 500,
    disabled = false,
    className = "",
    contentClassName = "",
    indicatorVariant = "default",
    useLenis = false,
    lenisDurationMs = 1000,
    lenisLerp = 0.1,
    "data-overlay": dataOverlayProp,
    disableBelow = 0,
    ...rest
  } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);
  const lenisRef = useRef<Lenis | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const externalOverlayRef = useRef<HTMLElement | null>(null);

  const resolvedHeight = typeof height === "number" ? `${height}px` : height;
  const accessibleLabel = props["aria-label"] || "Scrollable content";
  const overlayMode: "internal" | "external" | "none" =
    dataOverlayProp === "false" || dataOverlayProp === false
      ? "none"
      : typeof dataOverlayProp === "string" &&
        (dataOverlayProp as string).length > 0
      ? "external"
      : "internal";

  // Check if disabled on mobile
  useEffect(() => {
    if (disableBelow <= 0) return;
    const mql = window.matchMedia(`(max-width: ${disableBelow - 1}px)`);
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsDisabled(e.matches);
    };
    handler(mql);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [disableBelow]);

  // Resolve external overlay element once on mount/when selector changes
  useEffect(() => {
    if (overlayMode !== "external") {
      externalOverlayRef.current = null;
      return;
    }
    const selector = dataOverlayProp as string;
    if (typeof window === "undefined") return;
    externalOverlayRef.current = document.querySelector(
      selector
    ) as HTMLElement | null;
  }, [overlayMode, dataOverlayProp]);

  useEffect(() => {
    if (isDisabled) return;

    const wrapper = containerRef.current;
    const content = contentRef.current ?? wrapper;
    if (!wrapper) return;

    const computeScrollable = () => {
      const canScroll = (content?.scrollHeight ?? 0) > wrapper.clientHeight + 1;
      setIsScrollable(canScroll);
    };

    let cleanupNative = () => {};

    if (useLenis) {
      const lenis = new Lenis({
        wrapper: wrapper as unknown as HTMLElement,
        content: content as unknown as HTMLElement,
        duration: lenisDurationMs / 1000,
        lerp: lenisLerp,
        smoothWheel: true,
      });
      lenisRef.current = lenis;

      const onScroll = ({
        scroll,
        limit,
      }: {
        scroll: number;
        limit: number;
      }) => {
        const prog = limit > 0 ? Math.min(1, Math.max(0, scroll / limit)) : 0;
        setScrollProgress(prog);
      };
      lenis.on("scroll", onScroll);

      const raf = (time: number) => {
        lenis.raf(time);
        rafIdRef.current = requestAnimationFrame(raf);
      };
      rafIdRef.current = requestAnimationFrame(raf);

      const resizeObserver = new ResizeObserver(() => computeScrollable());
      if (content) resizeObserver.observe(content);
      resizeObserver.observe(wrapper);

      computeScrollable();

      cleanupNative = () => {
        lenis.off("scroll", onScroll);
        if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
        resizeObserver.disconnect();
        lenis.destroy();
        lenisRef.current = null;
      };
    } else {
      const updateState = () => {
        computeScrollable();
        const total = Math.max(
          0,
          (content?.scrollHeight ?? 0) - wrapper.clientHeight
        );
        const prog =
          total > 0 ? Math.min(1, Math.max(0, wrapper.scrollTop / total)) : 0;
        setScrollProgress(prog);
      };
      updateState();
      wrapper.addEventListener("scroll", updateState, { passive: true });
      const resizeObserver = new ResizeObserver(updateState);
      resizeObserver.observe(wrapper);
      if (content) resizeObserver.observe(content);
      cleanupNative = () => {
        wrapper.removeEventListener("scroll", updateState);
        resizeObserver.disconnect();
      };
    }

    return () => {
      cleanupNative();
    };
  }, [useLenis, lenisDurationMs, lenisLerp, isDisabled]);

  // Pass scroll to page when inner container is at top/bottom
  useEffect(() => {
    if (isDisabled || useLenis) return;
    const wrapper = containerRef.current;
    if (!wrapper) return;

    const handleWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = wrapper;
      const atTop = scrollTop <= 0 && e.deltaY < 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1 && e.deltaY > 0;
      if (atTop || atBottom) {
        // Don't prevent default — let the page scroll naturally
        return;
      }
      // Only stop propagation when actually scrolling inside
      e.stopPropagation();
    };

    wrapper.addEventListener("wheel", handleWheel, { passive: true });
    return () => wrapper.removeEventListener("wheel", handleWheel);
  }, [isDisabled, useLenis]);

  // Drive external overlay opacity
  useEffect(() => {
    if (overlayMode !== "external") return;
    const el = externalOverlayRef.current;
    if (!el) return;
    if (isDisabled) {
      el.style.opacity = "0";
      return;
    }
    el.style.opacity = isScrollable ? String(1 - scrollProgress) : "0";
  }, [overlayMode, isScrollable, scrollProgress, isDisabled]);

  // Disabled mode: render children directly without scroll container
  if (disabled || isDisabled) {
    return (
      <div className={`relative ${className}`} {...rest}>
        <div className={contentClassName}>{children}</div>
      </div>
    );
  }

  const handleKeyboardScroll = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const wrapper = containerRef.current;
    if (!wrapper || !isScrollable) return;

    const pageStep = Math.max(80, wrapper.clientHeight * 0.85);
    let nextTop: number | null = null;

    if (event.key === "ArrowDown") nextTop = wrapper.scrollTop + 56;
    if (event.key === "ArrowUp") nextTop = wrapper.scrollTop - 56;
    if (event.key === "PageDown" || event.key === " ") nextTop = wrapper.scrollTop + pageStep;
    if (event.key === "PageUp") nextTop = wrapper.scrollTop - pageStep;
    if (event.key === "Home") nextTop = 0;
    if (event.key === "End") nextTop = wrapper.scrollHeight;

    if (nextTop === null) return;

    event.preventDefault();

    const clampedTop = Math.max(0, Math.min(nextTop, wrapper.scrollHeight - wrapper.clientHeight));

    if (useLenis && lenisRef.current) {
      lenisRef.current.scrollTo(clampedTop, { immediate: false });
      return;
    }

    wrapper.scrollTo({ top: clampedTop, behavior: "smooth" });
  };

  return (
    <div
      ref={wrapperRef}
      className={`relative ${className}`}
      style={{ height: resolvedHeight }}
      {...rest}
    >
      <div
        ref={containerRef}
        data-lenis-prevent
        className={`${
          useLenis ? "overflow-hidden" : "scrollbar-hide overflow-y-auto"
        } overscroll-auto touch-pan-y h-full focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-theme/30 rounded-[inherit]`}
        tabIndex={isScrollable ? 0 : undefined}
        role={isScrollable ? "region" : undefined}
        aria-label={isScrollable ? accessibleLabel : undefined}
        onKeyDown={handleKeyboardScroll}
        style={
          overlayMode === "internal" && isScrollable && scrollProgress < 1
            ? {
                maskImage: `linear-gradient(to bottom, black 0%, black ${Math.round(65 + scrollProgress * 35)}%, transparent 100%)`,
                WebkitMaskImage: `linear-gradient(to bottom, black 0%, black ${Math.round(65 + scrollProgress * 35)}%, transparent 100%)`,
              }
            : undefined
        }
      >
        <div ref={contentRef} className={`pr-2 ${contentClassName}`}>
          {children}
        </div>
      </div>

      {/* Glass pill scroll indicator */}
      {isScrollable && scrollProgress < 0.9 && (
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none"
          style={{ opacity: Math.max(0, 1 - scrollProgress * 3) }}
        >
          {indicatorVariant === "contrast" && (
            <div className="absolute inset-0 scale-[1.75] rounded-full bg-[radial-gradient(circle,_rgba(17,10,39,0.85)_0%,_rgba(17,10,39,0.55)_45%,_rgba(17,10,39,0)_100%)] blur-[10px]" />
          )}
          <div
            className={`relative flex items-center justify-center w-9 h-9 rounded-full backdrop-blur-md shadow-lg ${
              indicatorVariant === "contrast"
                ? "border border-white/30 bg-[#1B113B]/85"
                : "border border-white/20 bg-white/10"
            }`}
            style={{ animation: "pillBounce 2.5s ease-in-out infinite" }}
          >
            <svg
              className={`w-4 h-4 ${
                indicatorVariant === "contrast"
                  ? "text-white/90"
                  : "text-white/70"
              }`}
              viewBox="0 0 16 16"
              fill="none"
            >
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes pillBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(4px); }
        }
      `}</style>
    </div>
  );
};

export default ScrollableContent;
